import { faXmark } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useMemo, useState } from "react";
import { FormattedMessage } from "react-intl";
import { api_getValueOrErrorMessage } from "../../Api/util";
import { displayPopin } from "../../Hook/popins";
import { dismiss, isDismissed, scopedKey } from "../../Hook/storage";
import useUser from "../../Hook/user";

// Five minutes, unchanged — but under a per-account key with the shared expiry
// encoding (CL1); the old ms-at-dismissal key is simply abandoned.
const DISMISS_SECONDS = 5 * 60;
const DISMISS_KEY = "emailconfirm:dismissed";
const RESEND_LOCK_SECONDS = 60;

/**
 * The email-confirm strip (Shared chrome board, turn 5a): a single line on the
 * accent tint between the header and the scroll container, hairline above and
 * below. Its height is reserved from first paint, so nothing shifts when the
 * user payload lands — the strip fades in place, or collapses once the payload
 * says the account is confirmed (or a guest).
 *
 * Resend reports back in the same strip — no toast, no popin — and locks for
 * 60 seconds, matching the emailconfirm popin's own lock. Dismiss keeps the
 * five-minute rule under the existing storage key.
 */
function EmailConfirmBanner() {
  const user = useUser();
  const display = displayPopin();
  const [dismissNonce, setDismissNonce] = useState(0);
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [resendLock, setResendLock] = useState(0);
  const dismissed = useMemo(() => isDismissed(scopedKey(DISMISS_KEY)), [dismissNonce, user?.informations?.id]);

  useEffect(() => {
    if (resendLock <= 0) return;
    const timer = setTimeout(() => setResendLock(resendLock - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendLock]);

  const informations = user?.informations as any;
  // Reserved until the payload lands; collapsed the moment it answers no.
  const pending = !informations;
  const needed =
    !!informations &&
    informations.type !== "guest" &&
    !informations.is_email_confirmed &&
    !dismissed;

  const resend = () => {
    if (resendLock > 0) return;
    setResendLock(RESEND_LOCK_SECONDS);
    api_getValueOrErrorMessage("user/email/send_confirmation_message").then(() => {
      setSentTo(informations?.email ?? "");
    });
  };

  return (
    <div
      data-testid="email-confirm-banner"
      aria-hidden={!needed}
      className={
        "w-full overflow-hidden transition-[height,opacity] duration-fade " +
        "bg-[color-mix(in_srgb,var(--s4s-accent)_10%,transparent)] " +
        "shadow-[inset_0_1px_0_color-mix(in_srgb,var(--s4s-accent)_28%,transparent),inset_0_-1px_0_color-mix(in_srgb,var(--s4s-accent)_28%,transparent)] " +
        (pending
          ? "h-16 sm:h-11 opacity-0 pointer-events-none"
          : needed
            ? "h-16 sm:h-11 opacity-100"
            : "h-0 opacity-0 pointer-events-none")
      }
    >
      {needed && (
      <div className="relative max-w-7xl mx-auto h-full px-page-x sm:px-6 flex flex-col sm:flex-row sm:items-center justify-center sm:justify-start gap-0.5 sm:gap-4">
        <div className="text-meta sm:text-row text-accent min-w-0 truncate">
          {sentTo !== null ? (
            <FormattedMessage id="emailbanner__sent" values={{ email: sentTo }} />
          ) : (
            <FormattedMessage id="emailbanner__text" />
          )}
        </div>
        <div className="flex items-center gap-4 flex-none">
          <button
            type="button"
            data-testid="email-banner-confirm"
            onClick={() => display("emailconfirm")}
            className="text-meta sm:text-row font-medium text-accent underline underline-offset-2"
          >
            <FormattedMessage id="emailbanner__confirm" />
          </button>
          <button
            type="button"
            data-testid="email-banner-resend"
            onClick={resend}
            disabled={resendLock > 0}
            className="text-meta sm:text-row text-muted disabled:opacity-disabled"
          >
            <FormattedMessage id="emailbanner__resend" />
            {resendLock > 0 && <> ({resendLock}s)</>}
          </button>
        </div>
        <button
          type="button"
          data-testid="email-banner-dismiss"
          onClick={() => {
            dismiss(scopedKey(DISMISS_KEY), DISMISS_SECONDS);
            setDismissNonce((n) => n + 1);
          }}
          className="absolute right-2 sm:static sm:ml-auto w-7 h-7 rounded-pill text-muted flex items-center justify-center hover:bg-tint-hover flex-none self-center"
          aria-label="close"
        >
          <FontAwesomeIcon icon={faXmark} />
        </button>
      </div>
      )}
    </div>
  );
}

export default EmailConfirmBanner;
